import React, { useEffect, useState, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getAllEvents,
  startKokoEventPayment,
} from "../services/eventService";
import PaymentService from "../services/PaymentService";
import { getCoinBalance } from "../services/coinService";
import NavUser from "../components/NavUser";
import SEO from "../components/SEO";
import "./EventBooking.css";
import {
  FaArrowLeft,
  FaShieldAlt,
  FaTicketAlt,
  FaCoins,
  FaUserCheck,
  FaLock,
  FaCalendarCheck,
  FaCheckCircle,
  FaInfoCircle,
} from "react-icons/fa";

const EventBooking = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const initialData = location.state || {};
  const [event, setEvent] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [quantity, setQuantity] = useState(initialData.quantity || 1);
  const [coinBalance, setCoinBalance] = useState(0);
  const [applyCoins, setApplyCoins] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("payments_lk"); // Payments.lk is primary gateway
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [user, setUser] = useState(null);

  // Load user data
  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const parsed = JSON.parse(userData);
        setUser(parsed);
      } catch (e) {
        console.error("Failed to parse user from localStorage", e);
      }
    }
  }, []);

  // Fetch Event details and ticket info
  useEffect(() => {
    const fetchEventData = async () => {
      try {
        setLoading(true);
        const data = await getAllEvents();
        const foundEvent = data.find((e) => e.id === parseInt(id));

        if (foundEvent) {
          setEvent(foundEvent);
          if (foundEvent.tickets && foundEvent.tickets.length > 0) {
            const matchedTicket =
              foundEvent.tickets.find((t) => t.id === initialData.ticketId) ||
              foundEvent.tickets[0];
            setSelectedTicket(matchedTicket);
          }
        } else {
          toast.error("Event not found");
        }
      } catch (err) {
        console.error("Error loading event:", err);
        toast.error("Failed to load event details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchEventData();
  }, [id, initialData.ticketId]);

  // Fetch user coin balance
  useEffect(() => {
    const fetchCoins = async () => {
      if (user && user.id) {
        try {
          const balance = await getCoinBalance(user.id);
          setCoinBalance(balance || 0);
        } catch (err) {
          console.error("Error fetching coin balance:", err);
        }
      }
    };
    fetchCoins();
  }, [user]);

  // Price calculations
  const pricePerTicket = useMemo(() => {
    return selectedTicket ? parseFloat(selectedTicket.price) || 0 : 0;
  }, [selectedTicket]);

  const baseSubtotal = useMemo(() => {
    return pricePerTicket * quantity;
  }, [pricePerTicket, quantity]);

  const maxCoinDiscount = useMemo(() => {
    if (!applyCoins || coinBalance <= 0) return 0;
    // 1 coin = 1 LKR, can cover up to total subtotal
    return Math.min(coinBalance, baseSubtotal);
  }, [applyCoins, coinBalance, baseSubtotal]);

  const finalAmount = useMemo(() => {
    const total = baseSubtotal - maxCoinDiscount;
    return total > 0 ? total : 0;
  }, [baseSubtotal, maxCoinDiscount]);

  const kokoInstallment = useMemo(() => {
    return (finalAmount / 3).toFixed(2);
  }, [finalAmount]);

  const maxAvailableTickets = selectedTicket?.count || 10;

  const handleIncrement = () => {
    if (quantity < maxAvailableTickets) {
      setQuantity((prev) => prev + 1);
    } else {
      toast.info(`Maximum ${maxAvailableTickets} tickets available`);
    }
  };

  const handleDecrement = () => {
    if (quantity > 1) {
      setQuantity((prev) => prev - 1);
    }
  };

  // KOKO Payment Flow
  const handleKokoPayment = async () => {
    if (!user) {
      toast.warning("Please login to proceed with booking");
      navigate("/login");
      return;
    }

    if (!event || !selectedTicket) {
      toast.error("Event or ticket information is missing");
      return;
    }

    try {
      setIsProcessing(true);

      const timestamp = Date.now();
      const orderId = `EVT_${event.id}_${selectedTicket.id}_${timestamp}`;

      const payload = {
        orderId,
        order_id: orderId,
        amount: finalAmount,
        userId: user.id,
        eventId: event.id,
        ticketId: selectedTicket.id,
        quantity: quantity,
        user: {
          id: user.id,
          firstName: user.full_name ? user.full_name.split(" ")[0] : "Guest",
          lastName: user.full_name ? user.full_name.split(" ").slice(1).join(" ") : "User",
          email: user.email || "guest@soloway.lk",
          phone: user.phone || user.phone_number || "0777904054",
          full_name: user.full_name || "Guest User",
        },
        event: {
          id: event.id,
          event_id: event.id,
          ticket_id: selectedTicket.id,
          quantity: quantity,
          title: event.title,
        },
        custom_1: user.id,
        custom_2: `${event.id}|${selectedTicket.id}|${quantity}`,
      };

      const response = await startKokoEventPayment(payload);

      if (!response || !response.actionUrl || !response.fields) {
        throw new Error(response?.error || "Invalid response from KOKO gateway service");
      }

      const { actionUrl, fields } = response;

      // Construct dynamic hidden form and submit directly to KOKO gateway
      const form = document.createElement("form");
      form.method = "POST";
      form.action = actionUrl;
      form.style.display = "none";

      Object.entries(fields).forEach(([key, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = typeof value === "object" ? JSON.stringify(value) : value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      console.error("KOKO Payment Initiation Error:", err);
      toast.error(err.message || "Failed to initialize KOKO payment. Please try again.");
      setIsProcessing(false);
    }
  };

  // Payments.lk Hosted Payment Flow
  const handlePaymentsLkPayment = async () => {
    if (!user) {
      toast.warning("Please login to proceed with booking");
      navigate("/login");
      return;
    }

    if (!event || !selectedTicket) {
      toast.error("Event or ticket information is missing");
      return;
    }

    try {
      setIsProcessing(true);
      toast.info("Connecting to Payments.lk secure checkout...");

      const session = await PaymentService.createPaymentsLkCheckout({
        bookingType: "event",
        bookingId: selectedTicket.id,
        quantity: quantity,
        clientType: "web",
      });

      if (session && session.checkoutUrl) {
        window.location.href = session.checkoutUrl;
      } else {
        throw new Error("Unable to create Payments.lk checkout session");
      }
    } catch (err) {
      console.error("Payments.lk Error:", err);
      toast.error(err.response?.data?.error || err.message || "Failed to start Payments.lk payment");
      setIsProcessing(false);
    }
  };

  const handleProceedToPayment = () => {
    if (paymentMethod === "koko") {
      handleKokoPayment();
    } else {
      handlePaymentsLkPayment();
    }
  };

  if (loading) {
    return (
      <div className="event-booking-wrapper">
        <NavUser />
        <div className="eb-loading-wrapper">
          <div className="eb-loading-spinner-large"></div>
          <h3 style={{ fontSize: "20px", fontWeight: 700, color: "#1e293b", margin: 0 }}>
            Loading Event Details...
          </h3>
          <p style={{ color: "#64748b", marginTop: "8px" }}>
            Preparing your checkout experience
          </p>
        </div>
      </div>
    );
  }

  if (!event || !selectedTicket) {
    return (
      <div className="event-booking-wrapper">
        <NavUser />
        <div className="eb-error-wrapper">
          <FaInfoCircle style={{ fontSize: "40px", color: "#ef4444", marginBottom: "16px" }} />
          <h3 style={{ fontSize: "22px", fontWeight: 800, color: "#1e293b" }}>
            Event Not Available
          </h3>
          <p style={{ color: "#64748b", margin: "10px 0 24px" }}>
            The event you are looking for is either unavailable or has expired.
          </p>
          <button className="back-btn-pill" onClick={() => navigate("/event")}>
            <FaArrowLeft /> Browse Other Events
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="event-booking-wrapper">
      <SEO
        title={`${event.title || "Event Tickets"} | Soloway Sri Lanka`}
        description={event.description ? event.description.substring(0, 155) : "Book verified Sri Lanka event tickets with secure Payments.lk online card checkout."}
        canonicalPath={`/events/${id}`}
      />
      <NavUser />
      <ToastContainer position="top-right" autoClose={4000} />

      <div className="event-booking-container">
        {/* Header Navigation */}
        <div className="booking-page-header">
          <button className="back-btn-pill" onClick={() => navigate("/event")}>
            <FaArrowLeft /> Back to Events
          </button>
          <div className="booking-header-titles">
            <h1>Confirm Your Event Booking</h1>
            <p>Review event ticket details and complete your secure checkout</p>
          </div>
          <div className="secure-badge-top">
            <FaShieldAlt /> Secure Checkout
          </div>
        </div>

        {/* 2-Column Responsive Checkout Grid */}
        <div className="event-booking-grid">
          {/* Left Column: Details & Selection */}
          <div className="eb-main-col">
            {/* Event Details Card */}
            <div className="eb-card">
              <div className="card-heading">
                <FaCalendarCheck className="card-heading-icon" />
                <span>Event Information</span>
              </div>
              <div className="event-hero-box">
                {event.image ? (
                  <img
                    src={
                      event.image.startsWith("http")
                        ? event.image
                        : `${(process.env.REACT_APP_API_URL || "https://api.soloway.lk").replace(
                            "/api",
                            ""
                          )}${event.image}`
                    }
                    alt={event.title}
                    className="event-banner-img"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : (
                  <div
                    className="event-banner-img"
                    style={{
                      background: "linear-gradient(135deg, #0284c7 0%, #0369a1 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontSize: "32px",
                    }}
                  >
                    <FaTicketAlt />
                  </div>
                )}
                <div className="event-hero-details">
                  <span className="event-tag-pill">Soloway Verified Event</span>
                  <h2 className="event-main-title">{event.title}</h2>
                  <p className="event-main-desc">{event.description}</p>
                </div>
              </div>
            </div>

            {/* Ticket Configuration & Quantity Card */}
            <div className="eb-card">
              <div className="card-heading">
                <FaTicketAlt className="card-heading-icon" />
                <span>Ticket Selection</span>
              </div>

              <div className="ticket-spec-grid">
                <div className="ticket-spec-item">
                  <span className="ticket-spec-label">Ticket Category</span>
                  <span className="ticket-spec-value">{selectedTicket.category}</span>
                </div>
                <div className="ticket-spec-item">
                  <span className="ticket-spec-label">Price Per Ticket</span>
                  <span className="ticket-spec-value highlight">
                    Rs. {pricePerTicket.toLocaleString()}
                  </span>
                </div>
                <div className="ticket-spec-item">
                  <span className="ticket-spec-label">Availability</span>
                  <span className="ticket-spec-value">
                    {selectedTicket.count > 0 ? `${selectedTicket.count} Left` : "Available"}
                  </span>
                </div>
              </div>

              {/* Quantity Stepper */}
              <div className="quantity-control-panel">
                <div className="qty-label-group">
                  <span className="qty-label-main">Number of Tickets</span>
                  <span className="qty-label-sub">Select quantity for attendees</span>
                </div>
                <div className="qty-stepper">
                  <button
                    type="button"
                    className="qty-btn"
                    onClick={handleDecrement}
                    disabled={quantity <= 1 || isProcessing}
                  >
                    -
                  </button>
                  <span className="qty-count">{quantity}</span>
                  <button
                    type="button"
                    className="qty-btn"
                    onClick={handleIncrement}
                    disabled={quantity >= maxAvailableTickets || isProcessing}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {/* Soloway Coins Rewards Card */}
            <div className="eb-card">
              <div className="card-heading">
                <FaCoins className="card-heading-icon" style={{ color: "#d97706" }} />
                <span>Soloway Coin Rewards</span>
              </div>
              <div className="coin-rewards-box">
                <div className="coin-details-left">
                  <div className="coin-icon-bubble">🪙</div>
                  <div>
                    <h4 className="coin-title-text">Redeem Your Coins</h4>
                    <p className="coin-balance-sub">
                      Available: {coinBalance} Coins (Rs. {coinBalance}.00)
                    </p>
                  </div>
                </div>

                {coinBalance > 0 ? (
                  <label className="coin-apply-checkbox">
                    <input
                      type="checkbox"
                      checked={applyCoins}
                      onChange={(e) => setApplyCoins(e.target.checked)}
                      disabled={isProcessing}
                    />
                    <span>{applyCoins ? "Applied" : "Apply Coins"}</span>
                  </label>
                ) : (
                  <span style={{ fontSize: "13px", color: "#94a3b8", fontWeight: 600 }}>
                    0 Coins Available
                  </span>
                )}
              </div>
            </div>

            {/* Buyer Details Card */}
            {user && (
              <div className="eb-card">
                <div className="card-heading">
                  <FaUserCheck className="card-heading-icon" />
                  <span>Attendee & Delivery Info</span>
                </div>
                <div className="attendee-info-grid">
                  <div className="attendee-field">
                    <span>Full Name</span>
                    <span>{user.full_name || "Guest User"}</span>
                  </div>
                  <div className="attendee-field">
                    <span>Email Address</span>
                    <span>{user.email || "Not Provided"}</span>
                  </div>
                  <div className="attendee-field">
                    <span>Phone Number</span>
                    <span>{user.phone || user.phone_number || "0777123456"}</span>
                  </div>
                </div>
                <p
                  style={{
                    fontSize: "12px",
                    color: "#94a3b8",
                    marginTop: "16px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <FaCheckCircle style={{ color: "#10b981" }} />
                  E-tickets and booking confirmation will be instantly delivered to your email.
                </p>
              </div>
            )}
          </div>

          {/* Right Column: Sticky Summary & Payment Gateways */}
          <div className="sticky-checkout-col">
            <div className="summary-card-premium">
              <h3
                style={{
                  fontSize: "20px",
                  fontWeight: 800,
                  color: "#0f172a",
                  marginBottom: "20px",
                }}
              >
                Order Summary
              </h3>

              {/* Order Breakdown */}
              <div className="order-breakdown-list">
                <div className="breakdown-row">
                  <span>
                    {selectedTicket.category} x {quantity}
                  </span>
                  <span>Rs. {baseSubtotal.toLocaleString()}</span>
                </div>

                {applyCoins && maxCoinDiscount > 0 && (
                  <div className="breakdown-row discount">
                    <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <FaCoins /> Soloway Coin Discount
                    </span>
                    <span>- Rs. {maxCoinDiscount.toLocaleString()}</span>
                  </div>
                )}

                <div className="breakdown-divider"></div>

                <div className="breakdown-row total">
                  <span>Total Payable</span>
                  <span className="total-price-large">
                    Rs. {finalAmount.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Payment Method Selector */}
              <span className="payment-selector-title">Select Payment Method</span>
              <div className="payment-options-stack">
                {/* KOKO BNPL Card */}
                <div
                  className={`payment-method-card ${
                    paymentMethod === "koko" ? "selected" : ""
                  }`}
                  onClick={() => setPaymentMethod("koko")}
                >
                  <div className="pm-left-meta">
                    <div className="pm-radio-circle">
                      {paymentMethod === "koko" && <div className="pm-radio-dot" />}
                    </div>
                    <div className="pm-info-text">
                      <span className="pm-title">KOKO (Buy Now, Pay Later)</span>
                      <span className="pm-sub">
                        3 x Rs. {kokoInstallment} / month • 0% Interest
                      </span>
                    </div>
                  </div>
                  <span className="pm-badge-tag koko">KOKO</span>
                </div>

                {/* Payments.lk Card */}
                <div
                  className={`payment-method-card ${
                    paymentMethod === "payments_lk" ? "selected" : ""
                  }`}
                  onClick={() => setPaymentMethod("payments_lk")}
                >
                  <div className="pm-left-meta">
                    <div className="pm-radio-circle">
                      {paymentMethod === "payments_lk" && <div className="pm-radio-dot" />}
                    </div>
                    <div className="pm-info-text">
                      <span className="pm-title">Payments.lk Gateway</span>
                      <span className="pm-sub">
                        Cards, Mobile Wallets & Internet Banking
                      </span>
                    </div>
                  </div>
                  <span className="pm-badge-tag payments-lk" style={{ background: "#2563eb", color: "#fff" }}>Payments.lk</span>
                </div>
              </div>

              {/* CTA Button */}
              <button
                type="button"
                className="checkout-submit-btn"
                onClick={handleProceedToPayment}
                disabled={isProcessing || finalAmount <= 0}
              >
                {isProcessing ? (
                  <>
                    <div className="eb-spinner"></div>
                    <span>Processing Gateway...</span>
                  </>
                ) : (
                  <>
                    <FaLock />
                    <span>
                      Pay Rs. {finalAmount.toLocaleString()}{" "}
                      {paymentMethod === "koko" ? "with KOKO" : "with Payments.lk"}
                    </span>
                  </>
                )}
              </button>

              <div className="checkout-guarantee-note">
                <FaShieldAlt /> 256-Bit SSL Encrypted & Verified Payment
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventBooking;
